import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from '../utils/api';

// Payment form component (must be inside Elements provider)
const PaymentForm = ({ payment, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/${payment.paymentId}/success`,
        },
      });

      if (submitError) {
        setError(submitError.message);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {message && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
          <p className="text-sm">{message}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className={`w-full btn-primary text-lg py-4 ${
          processing ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {processing ? 'Processing...' : `Pay €${payment.amount.toFixed(2)}`}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Your payment is secured by Stripe. We never see your card details.
      </p>
    </form>
  );
};

const PaymentPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [partyId, setPartyId] = useState(null);
  const [partyRole, setPartyRole] = useState(null); // 'debtor' or 'creditor'
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Get partyId from URL query params
    const params = new URLSearchParams(window.location.search);
    const pid = params.get('partyId');
    if (pid) {
      setPartyId(pid);
    }
    loadPaymentDetails();
  }, [paymentId]);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      const data = await api.request(`/payment/${paymentId}`);
      setPayment(data);

      // Only initialize Stripe for real (non-mock) payments
      if (data.stripePublishableKey && data.clientSecret && !data.clientSecret.includes('mock')) {
        setStripePromise(loadStripe(data.stripePublishableKey));
      }
    } catch (err) {
      console.error('Load payment error:', err);
      setError(err.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    alert('Payment submitted successfully! You will receive a confirmation email shortly.');
    navigate(`/payment/${paymentId}/success`);
  };

  const handleMockPayment = async () => {
    if (!window.confirm(`Confirm that you have made the payment of ${formatCurrency(payment.amount)}?`)) {
      return;
    }

    try {
      setProcessing(true);
      // Call the mark-paid endpoint
      await api.request(`/payment/${paymentId}/mark-paid`, {
        method: 'POST'
      });

      alert('Payment indication recorded successfully!\n\nEmails have been sent to both parties.\n\nThe creditor will verify receipt of funds in their bank account.');

      // Reload payment details to show updated status
      await loadPaymentDetails();
    } catch (err) {
      alert(err.message || 'Failed to record payment indication');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!window.confirm(`Confirm that you have received the payment of ${formatCurrency(payment.amount)} in your bank account?`)) {
      return;
    }

    if (!partyId) {
      alert('Party ID is required to confirm payment');
      return;
    }

    try {
      setProcessing(true);
      await api.request(`/payment/${paymentId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ partyId })
      });

      alert('Payment confirmed successfully!\n\nThe debtor has been notified.');

      // Reload payment details to show updated status
      await loadPaymentDetails();
    } catch (err) {
      alert(err.message || 'Failed to confirm payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclinePayment = async () => {
    const reason = window.prompt('Please provide a reason for declining (optional):', 'Payment not received in bank account');

    if (reason === null) {
      return; // User cancelled
    }

    if (!partyId) {
      alert('Party ID is required to decline payment');
      return;
    }

    try {
      setProcessing(true);
      await api.request(`/payment/${paymentId}/decline`, {
        method: 'POST',
        body: JSON.stringify({ partyId, reason })
      });

      alert('Payment declined.\n\nThe debtor has been notified to check their payment status.');

      // Reload payment details to show updated status
      await loadPaymentDetails();
    } catch (err) {
      alert(err.message || 'Failed to decline payment');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTerm = (term) => {
    const termNames = {
      'mutual_release': 'Mutual Release of All Claims',
      'no_admission': 'No Admission of Liability',
      'confidentiality': 'Confidentiality Agreement',
      'future_work_clause': 'Future Business Relationship',
      'late_payment_penalty_5pct': 'Late Payment Penalty - 5%'
    };
    return termNames[term] || term;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (payment.status !== 'pending') {
    // Determine if user is creditor viewing payment indication
    const isCreditorViewingIndication = payment.status === 'debtor_indicated_paid' && partyId;

    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-2xl w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">
              {payment.status === 'succeeded' || payment.status === 'confirmed' ? '✅' :
               payment.status === 'debtor_indicated_paid' ? '⏳' :
               payment.status === 'declined' ? '❌' : '📋'}
            </div>
            <h2 className="text-xl font-bold mb-2">
              {payment.status === 'succeeded' || payment.status === 'confirmed'
                ? 'Payment Completed'
                : payment.status === 'debtor_indicated_paid'
                ? isCreditorViewingIndication ? 'Payment Indication - Awaiting Your Confirmation' : 'Payment Indication Recorded'
                : payment.status === 'declined'
                ? 'Payment Declined'
                : 'Payment Status'}
            </h2>
            <p className="text-gray-600 mb-4">
              {payment.status === 'succeeded' || payment.status === 'confirmed'
                ? 'This payment has been completed and verified.'
                : payment.status === 'debtor_indicated_paid'
                ? isCreditorViewingIndication
                  ? 'The debtor has indicated they made this payment. Please verify receipt in your bank account and confirm below.'
                  : 'You have indicated that payment was made. The creditor will verify receipt of funds in their bank account.'
                : payment.status === 'declined'
                ? 'The creditor has reported that the payment was not received in their bank account.'
                : `This payment is currently ${payment.status.replace(/_/g, ' ')}.`}
            </p>
            <p className="text-sm text-gray-500 mb-2">Payment ID: {payment.paymentId}</p>

            {/* Payment Details */}
            <div className="mt-4 bg-gray-50 rounded p-4 text-left">
              <p className="text-sm text-gray-800">
                <strong>Amount:</strong> {formatCurrency(payment.amount)}
              </p>
              {payment.paidAt && (
                <p className="text-sm text-gray-800">
                  <strong>Indicated Date:</strong> {new Date(payment.paidAt).toLocaleString('en-IE')}
                </p>
              )}
              {payment.confirmedAt && (
                <p className="text-sm text-green-700">
                  <strong>✓ Confirmed:</strong> {new Date(payment.confirmedAt).toLocaleString('en-IE')}
                </p>
              )}
              {payment.declinedAt && (
                <p className="text-sm text-red-700">
                  <strong>✗ Declined:</strong> {new Date(payment.declinedAt).toLocaleString('en-IE')}
                </p>
              )}
              {payment.declineReason && (
                <p className="text-sm text-red-700">
                  <strong>Reason:</strong> {payment.declineReason}
                </p>
              )}
            </div>

            {/* Creditor Actions for Payment Indication */}
            {isCreditorViewingIndication && (
              <div className="mt-6 space-y-3">
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-left">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Important:</strong> Only confirm if you have verified the payment in your bank account.
                    The debtor has indicated they made this payment, but you must verify actual receipt of funds.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleConfirmPayment}
                    disabled={processing}
                    className="btn-primary bg-green-600 hover:bg-green-700 text-white py-3"
                  >
                    {processing ? 'Processing...' : '✓ Confirm Payment Received'}
                  </button>
                  <button
                    onClick={handleDeclinePayment}
                    disabled={processing}
                    className="btn-secondary bg-red-600 hover:bg-red-700 text-white py-3"
                  >
                    {processing ? 'Processing...' : '✗ Payment Not Received'}
                  </button>
                </div>
              </div>
            )}

            {/* Status Info */}
            {payment.status === 'debtor_indicated_paid' && !isCreditorViewingIndication && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-4 text-left">
                <p className="text-sm text-blue-800">
                  <strong>✓ Recorded:</strong> {new Date(payment.paidAt || Date.now()).toLocaleString('en-IE')}
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Both parties have been notified by email. Please allow 2-3 business days for bank processing and creditor confirmation.
                </p>
              </div>
            )}

            {payment.status === 'declined' && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded p-4 text-left">
                <p className="text-sm text-red-800">
                  <strong>⚠️ Action Required:</strong> The creditor has not received your payment.
                  Please check your bank account and payment details. If you made the payment, contact your bank to trace the transaction.
                </p>
                <p className="text-xs text-red-600 mt-2">
                  This issue must be resolved within 5 business days to avoid settlement termination.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isMockPayment = payment.clientSecret && payment.clientSecret.includes('mock');

  const options = !isMockPayment ? {
    clientSecret: payment.clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#667eea',
      },
    },
  } : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Complete Your Payment</h1>
        <p className="text-gray-600">
          Secure payment for your settlement agreement
        </p>
      </div>

      {/* Payment Summary */}
      <div className="card bg-blue-50 border-2 border-blue-300 mb-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-blue-800 mb-2">Amount Due</h2>
          <p className="text-5xl font-bold text-blue-600 mb-3">
            {formatCurrency(payment.amount)}
          </p>
          <p className="text-sm text-gray-700">
            {payment.paymentType === 'lump_sum' ? 'Full settlement amount' : 'Initial payment'}
          </p>
          {payment.dueDate && (
            <p className="text-sm text-gray-700 mt-2">
              <strong>Due:</strong> {formatDate(payment.dueDate)}
            </p>
          )}
        </div>
      </div>

      {/* Settlement Information */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <h3 className="text-lg font-bold mb-3">Settlement Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Settlement:</span>
              <span className="font-semibold">{formatCurrency(payment.settlementAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">This Payment:</span>
              <span className="font-semibold">{formatCurrency(payment.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Type:</span>
              <span className="font-semibold capitalize">
                {payment.paymentType.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-bold mb-3">Case Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Dispute ID:</span>
              <span className="font-mono text-xs">{payment.disputeId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payment ID:</span>
              <span className="font-mono text-xs">{payment.paymentId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Structure */}
      {payment.structure && (
        <div className="card mb-6">
          <h3 className="text-lg font-bold mb-3">Payment Schedule</h3>
          {payment.structure.type === 'lump_sum' ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Single Payment:</strong> Full amount due in {payment.structure.dueInDays} days
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Initial Payment (Due Now)</p>
                    <p className="text-sm text-gray-600">Required to activate settlement</p>
                  </div>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(payment.structure.initial)}
                  </p>
                </div>
              </div>
              {payment.structure.installments && payment.structure.installments.map((inst, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold">Installment {idx + 1}</p>
                      <p className="text-sm text-gray-600">Due in {inst.dueInDays} days</p>
                    </div>
                    <p className="text-xl font-bold text-gray-700">
                      {formatCurrency(inst.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settlement Terms */}
      {payment.terms && payment.terms.length > 0 && (
        <div className="card mb-6">
          <h3 className="text-lg font-bold mb-3">Settlement Terms</h3>
          <div className="space-y-2">
            {payment.terms.map((term, idx) => (
              <div key={idx} className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✓</span>
                <p className="text-sm text-gray-700">{formatTerm(term)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Form */}
      <div className="card border-2 border-green-300">
        <h3 className="text-xl font-bold mb-4">Payment Method</h3>

        {isMockPayment ? (
          /* Mock Payment Interface for Development */
          <div className="space-y-4">
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
              <div className="flex items-start">
                <div className="text-2xl mr-3">⚠️</div>
                <div>
                  <h4 className="font-bold text-yellow-800 mb-2">Development Mode - Mock Payment</h4>
                  <p className="text-sm text-yellow-700">
                    This is a test payment. No real money will be charged.
                    Click the button below to simulate a successful payment.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
              <div className="text-center">
                <div className="text-4xl mb-3">💳</div>
                <p className="text-sm text-gray-600 mb-4">
                  Mock Payment Simulator
                </p>
                <div className="space-y-2 text-left max-w-sm mx-auto mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-bold">{formatCurrency(payment.amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Payment ID:</span>
                    <span className="font-mono text-xs">{payment.paymentId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Status:</span>
                    <span className="text-yellow-600 font-semibold">Test Mode</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleMockPayment}
              className="w-full btn-primary text-lg py-4"
            >
              Complete Mock Payment - {formatCurrency(payment.amount)}
            </button>

            <p className="text-xs text-gray-500 text-center">
              Configure STRIPE_SECRET_KEY in production to enable real payments
            </p>
          </div>
        ) : stripePromise && payment.clientSecret ? (
          /* Real Stripe Payment Form */
          <Elements stripe={stripePromise} options={options}>
            <PaymentForm payment={payment} onSuccess={handlePaymentSuccess} />
          </Elements>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <p className="text-sm text-yellow-800">
              Payment system is not configured. Please contact support.
            </p>
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <div className="flex items-start">
          <div className="text-2xl mr-3">🔒</div>
          <div>
            <h4 className="font-bold mb-2">Secure Payment</h4>
            <p className="text-sm text-gray-700">
              Your payment is processed securely through Stripe, a certified PCI Level 1 Service Provider.
              Your card information is encrypted and never stored on our servers.
            </p>
          </div>
        </div>
      </div>

      {/* Help */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600 mb-2">
          Questions about this payment?
        </p>
        <p className="text-sm text-gray-500">
          Contact support@haladik.com
        </p>
      </div>
    </div>
  );
};

export default PaymentPage;
